import React from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, CardMedia, useTheme } from '@mui/material';
import { Brain, FileSearch, BarChart3, Users, Clock, Shield, CheckCircle, Send} from 'lucide-react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const features = [
  {
    title: 'AI Resume Analysis',
    description: 'Our advanced AI analyzes resumes to identify the most qualified candidates based on skills, experience, and job requirements.',
    icon: <FileSearch size={40} />,
    color: '#3f51b5'
  },
  {
    title: 'Smart Candidate Matching',
    description: 'Match candidates to positions with precision using our intelligent algorithms that understand the nuances of job requirements.',
    icon: <Users size={40} />,
    color: '#f50057'
  },
  {
    title: 'Bias-Free Recruiting',
    description: 'Eliminate unconscious bias from your hiring process with our objective AI-powered candidate evaluation system.',
    icon: <Shield size={40} />,
    color: '#00bcd4'
  },
  {
    title: 'Comprehensive Analytics',
    description: 'Gain valuable insights into your recruiting process with detailed analytics and reporting features.',
    icon: <BarChart3 size={40} />,
    color: '#4caf50'
  },
  {
    title: 'Time-Saving Automation',
    description: 'Automate repetitive tasks in the recruitment process, allowing your team to focus on strategic activities.',
    icon: <Clock size={40} />,
    color: '#ff9800'
  },
  {
    title: 'Intelligent Learning',
    description: 'Our system continuously learns from your hiring decisions to improve candidate recommendations over time.',
    icon: <Brain size={40} />,
    color: '#9c27b0'
  },
  {
    title: 'Access to Forbes Top 250 Recruiters',
    description: "ARC Group’s award-winning team is available per hour in addition to RiC's search results, ensuring nuanced human insight and an accelerated hiring process without the full-time agency cost",
    icon: <Users size={40} />,
    color: '#3f51b5'
  },
  {
    title: 'Up to 75+ Job Boards Included',
    description: "Target both passive and active candidates instantly by posting your jobs across top platforms like Indeed, ZipRecruiter, Google Jobs, and more at (valued at $399/posting). ",
    icon: <Send size={40} />,
    color: '#ffa500'
  },
  {
    title: 'Smarter hiring starts here',
    description: "It doesn't matter whether you're hiring for one role or 50. By blending AI-driven technology with human-refined recruiting, we deliver results no traditional ATS, job board, or agency can match",
    icon: <Brain size={40} />,
    color: '#9c27b0'
  }
];

const Features = () => {
  const theme = useTheme();

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  return (
    <Box sx={{ py: 10, backgroundColor: '#f9f9f9' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography 
            variant="overline" 
            component="div"
            sx={{ 
              color: 'primary.main',
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 600,
              letterSpacing: 1.5,
              mb: 1
            }}
          >
            KEY FEATURES
          </Typography>
          <Typography 
            variant="h2" 
            component="h2"
            sx={{ 
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Revolutionize Your Hiring Process
          </Typography>
          <Typography 
            variant="subtitle1"
            sx={{ 
              color: 'text.secondary',
              maxWidth: '700px',
              mx: 'auto',
              mb: 4
            }}
          >
            Our AI-powered platform offers a comprehensive suite of tools designed to streamline your recruitment process and find the best talent.
          </Typography>
        </Box>

        <Box sx={{ mb: 6 }}>
          <Slider {...settings}>
            {features.map((feature, index) => (
              <Box key={index} sx={{ p: 2 }}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 15px 35px rgba(0,0,0,0.15)',
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      p: 3, 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: `${feature.color}10`,
                      color: feature.color
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Slider>
        </Box>

        <Box sx={{ mt: 8, position: 'relative' }}>
          <Card 
            sx={{ 
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            }}
          >
            <Grid container>
              <Grid item xs={12} md={6}>
                <CardMedia
                  component="img"
                  image="https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80"
                  alt="AI Recruiting Dashboard"
                  sx={{ 
                    height: '100%',
                    minHeight: { xs: '300px', md: 'auto' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CardContent sx={{ p: 5 }}>
                  <Typography 
                    variant="h4" 
                    component="h3" 
                    gutterBottom
                    sx={{ fontWeight: 700 }}
                  >
                    Powerful AI Resume Grading
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                    Our advanced AI technology analyzes resumes with unprecedented accuracy, identifying the most qualified candidates based on your specific job requirements.
                  </Typography>
                  
                  {/* <Grid container spacing={3}> */}

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box 
                          sx={{ 
                            mr: 2, 
                            color: 'primary.main',
                            backgroundColor: 'primary.light',
                            p: 1,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckCircle size={20} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Skill Matching
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Accurately identifies relevant skills
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box 
                          sx={{ 
                            mr: 2, 
                            color: 'primary.main',
                            backgroundColor: 'primary.light',
                            p: 1,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckCircle size={20} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Experience Analysis
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Evaluates quality of experience
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box 
                          sx={{ 
                            mr: 2, 
                            color: 'primary.main',
                            backgroundColor: 'primary.light',
                            p: 1,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckCircle size={20} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Talent Match Rating
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                           Ranks talent 1–10 based on a combination of match to role and qualifications
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                   <Grid item xs={12} sm={6}>
                   {/* <Grid item xs={12} sm={6} sx={{ gridColumn: {sm: '1/3'}, maxWidth:{sm:'50%'} }}> */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box 
                          sx={{ 
                            mr: 2, 
                            color: 'primary.main',
                            backgroundColor: 'primary.light',
                            p: 1,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckCircle size={20} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            RiC Talent Summary
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                           An AI-enhanced summary of each candidate's strengths, experience, and alignment with your job requirements
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Grid>
            </Grid>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default Features;
